import React from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import type { NextMatch } from '../../hooks/useHomeFeed';
import MatchRoleBadge from '../match/MatchRoleBadge';

function formatNextMatchDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === tomorrow.toDateString()) return 'Mañana';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}

interface NextMatchCardProps {
  match: NextMatch | null;
  currentUserId?: string;
  onPress?: (matchId: string) => void;
  onCreateMatch?: () => void;
}

export default function NextMatchCard({
  match,
  currentUserId,
  onPress,
  onCreateMatch,
}: NextMatchCardProps) {
  const navigate = useNavigate();

  const handlePress = (matchId: string) => {
    if (onPress) onPress(matchId);
    else navigate(`/match/${matchId}`);
  };

  const handleCreate = () => {
    if (onCreateMatch) onCreateMatch();
    else navigate('/create-match');
  };

  if (!match) {
    return (
      <div
        style={{
          marginBottom: theme.spacing.md,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          border: `1px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 48, marginBottom: theme.spacing.sm, color: theme.colors.textSecondary }}>📅</div>
        <h3 style={{ margin: '0 0 8px 0', color: theme.colors.text, fontFamily: theme.fonts.body, fontWeight: 600 }}>
          No tenés partidos próximos
        </h3>
        <p style={{ margin: '0 0 16px 0', color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
          ¡Creá uno o unite a un partido!
        </p>
        <button
          type="button"
          onClick={handleCreate}
          style={{
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            backgroundColor: theme.colors.primary,
            color: theme.colors.background,
            border: 'none',
            borderRadius: theme.borderRadius.button,
            fontSize: theme.fonts.sizeBody,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Crear Partido
        </button>
      </div>
    );
  }

  const dateText = formatNextMatchDate(match.date);

  return (
    <button
      type="button"
      tabIndex={0}
      onClick={() => handlePress(match.id)}
      onKeyDown={(e) => e.key === 'Enter' && handlePress(match.id)}
      style={{
        marginBottom: theme.spacing.md,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        border: `1px solid ${theme.colors.border}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        background: theme.colors.surface,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.sm,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <span style={{ fontSize: 20 }}>⚽</span>
          <span style={{ color: theme.colors.text, fontFamily: theme.fonts.body, fontWeight: 600 }}>
            Próximo Partido
          </span>
        </div>
        {currentUserId && (
          <MatchRoleBadge
            creatorId={match.creatorId}
            currentUserId={currentUserId}
            players={match.players}
            size="sm"
          />
        )}
      </div>
      <div style={{ marginBottom: theme.spacing.sm }}>
        <div style={{ color: theme.colors.text, fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{match.location}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
            📅 {dateText}, {match.time}
          </span>
          <span style={{ color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
            👥 {match.playersJoined}/{match.maxPlayers} jugadores
          </span>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{ color: theme.colors.primary, fontSize: theme.fonts.sizeSmall, fontWeight: 600 }}>
          Ver detalles →
        </span>
      </div>
    </button>
  );
}
